import React from 'react';
import { Link } from 'react-router-dom';
import { FaLock, FaArrowLeft, FaShieldAlt, FaKey, FaServer, FaUserSecret, FaCheckDouble, FaCode, FaNetworkWired } from 'react-icons/fa';
import SEO from '../components/SEO';

function Security() {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Security Architecture - How We Protect Your Files"
        description="Learn how MirageDocs protects your files with AES-256 client-side encryption, zero-knowledge architecture, and SRP password protection. Technical security documentation."
        url="https://miragedocs.com/security"
      />
      {/* Header */}
      <header className="w-full py-6 px-6 bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-gray-900 flex items-center gap-2" aria-label="MirageDocs home">
            <FaLock className="text-[#2186EB]" aria-hidden="true" />
            MirageDocs
          </Link>
          <Link to="/" className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
            <FaArrowLeft /> Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <FaShieldAlt className="text-4xl text-[#2186EB]" aria-hidden="true" />
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Security Architecture</h1>
          </div>

          <p className="text-gray-600 mb-4">
            MirageDocs is designed with security and privacy at its core. This document explains
            how we protect your files and data.
          </p>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
            <p className="text-green-800 font-medium">
              TL;DR: Your files are encrypted in your browser before upload. We never see your
              encryption keys or plaintext files. Even if our servers were compromised, your files
              would remain unreadable.
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            {/* Zero Knowledge */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaUserSecret className="text-[#2186EB]" aria-hidden="true" />
                Zero-Knowledge Architecture
              </h2>
              <p className="text-gray-700 mb-4">
                MirageDocs implements a true zero-knowledge architecture. This means:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Client-side encryption:</strong> All encryption happens in your browser using JavaScript</li>
                <li><strong>Key never stored:</strong> Your key is sent once, over TLS, only so we can compute a one-way hash of it for verification - the server never stores the key itself, only that hash. Share links carry the actual key in the URL fragment (after #), which browsers never send to any server</li>
                <li><strong>We cannot decrypt:</strong> We only ever hold a one-way hash of your key, never the key itself, so even with full database access, we cannot read your files</li>
                <li><strong>No backdoors:</strong> There is no "master key" or recovery mechanism we control</li>
              </ul>

              {/* Architecture Diagram */}
              <div className="bg-gray-50 rounded-lg p-6 my-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">How It Works</h3>
                <div className="space-y-4 text-sm">
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 bg-[#2186EB] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                    <div>
                      <strong className="text-gray-800">File Selection</strong>
                      <p className="text-gray-600">You select a file in your browser</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 bg-[#2186EB] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                    <div>
                      <strong className="text-gray-800">Key Generation</strong>
                      <p className="text-gray-600">A 256-bit random encryption key is generated in your browser using crypto-secure random</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 bg-[#2186EB] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                    <div>
                      <strong className="text-gray-800">Client-Side Encryption</strong>
                      <p className="text-gray-600">Your file is encrypted with AES-256 entirely in your browser</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 bg-[#2186EB] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                    <div>
                      <strong className="text-gray-800">Upload</strong>
                      <p className="text-gray-600">Only the encrypted blob is sent to our server over TLS</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 bg-[#2186EB] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">5</span>
                    <div>
                      <strong className="text-gray-800">Link Generation</strong>
                      <p className="text-gray-600">Share URL is created with key in fragment: <code className="bg-gray-200 px-1 rounded">miragedocs.com/download#token=...</code></p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">6</span>
                    <div>
                      <strong className="text-gray-800">Recipient Downloads</strong>
                      <p className="text-gray-600">Recipient's browser downloads encrypted file and decrypts it locally using the key from the URL</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Encryption Details */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaKey className="text-[#2186EB]" aria-hidden="true" />
                Encryption Specifications
              </h2>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-200 my-4">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-200 px-4 py-2 text-left">Component</th>
                      <th className="border border-gray-200 px-4 py-2 text-left">Specification</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2 font-medium">File Encryption</td>
                      <td className="border border-gray-200 px-4 py-2">AES-256-GCM (Authenticated Encryption)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-2 font-medium">Key Size</td>
                      <td className="border border-gray-200 px-4 py-2">256 bits (32 bytes)</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2 font-medium">Key Generation</td>
                      <td className="border border-gray-200 px-4 py-2">Web Crypto API (crypto.getRandomValues)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-2 font-medium">Password Protection</td>
                      <td className="border border-gray-200 px-4 py-2">SRP-6a (Secure Remote Password) protocol</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-200 px-4 py-2 font-medium">Password Hashing (Accounts)</td>
                      <td className="border border-gray-200 px-4 py-2">bcrypt with 12 rounds</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-200 px-4 py-2 font-medium">Transport</td>
                      <td className="border border-gray-200 px-4 py-2">TLS 1.2/1.3 (HTTPS only)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Password Protection */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaLock className="text-[#2186EB]" aria-hidden="true" />
                Password Protection (SRP Protocol)
              </h2>
              <p className="text-gray-700 mb-4">
                When you add a password to a shared file, we use the Secure Remote Password (SRP-6a) protocol.
                This is a zero-knowledge password proof system:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Password never sent:</strong> Your password is never transmitted to our server</li>
                <li><strong>Mathematical verification:</strong> Server stores only a "verifier" derived from your password</li>
                <li><strong>No replay attacks:</strong> Each authentication session uses fresh random values</li>
                <li><strong>Server cannot learn password:</strong> Even with the verifier, we cannot determine your password</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <strong>Technical Note:</strong> SRP is the same protocol used by Apple iCloud, 1Password, and other
                  security-focused services for password authentication.
                </p>
              </div>
            </section>

            {/* Infrastructure */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaServer className="text-[#2186EB]" aria-hidden="true" />
                Infrastructure Security
              </h2>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>HTTPS Only:</strong> All connections use TLS 1.2 or higher</li>
                <li><strong>HSTS:</strong> HTTP Strict Transport Security enforced</li>
                <li><strong>Rate Limiting:</strong> Protection against brute-force attacks</li>
                <li><strong>CSRF Protection:</strong> State-changing file-sharing requests require valid CSRF tokens</li>
                <li><strong>Bearer Token Authentication:</strong> Account sessions use short-lived JWTs sent via the Authorization header</li>
                <li><strong>Content Security Policy:</strong> Protection against XSS attacks</li>
                <li><strong>Auto-deletion:</strong> Files automatically removed after expiry or download limit</li>
              </ul>
            </section>

            {/* What We Can See */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaCheckDouble className="text-[#2186EB]" aria-hidden="true" />
                What We Can and Cannot See
              </h2>

              <div className="grid md:grid-cols-2 gap-6 my-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-bold text-red-800 mb-2">We CANNOT See:</h3>
                  <ul className="text-red-700 text-sm space-y-1">
                    <li>Your file contents</li>
                    <li>Your encryption key</li>
                    <li>File passwords</li>
                    <li>Decrypted filenames (for multi-file zips)</li>
                  </ul>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-bold text-green-800 mb-2">We CAN See:</h3>
                  <ul className="text-green-700 text-sm space-y-1">
                    <li>Encrypted blob size</li>
                    <li>Original filename</li>
                    <li>Upload/download timestamps</li>
                    <li>Expiry settings</li>
                    <li>Your email (if registered)</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Libraries */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaCode className="text-[#2186EB]" aria-hidden="true" />
                Open Source Libraries
              </h2>
              <p className="text-gray-700 mb-4">
                We use well-audited, secure cryptographic implementations:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Web Crypto API:</strong> Native browser AES-256-GCM implementation (hardware-accelerated, FIPS-compliant)</li>
                <li><strong>secure-remote-password:</strong> SRP-6a protocol implementation</li>
                <li><strong>bcrypt:</strong> Password hashing for user accounts</li>
              </ul>
              <p className="text-gray-700 mt-4">
                All cryptographic operations happen in the browser using the native Web Crypto API. You can inspect
                the network tab in your browser's developer tools to verify that only encrypted data is transmitted.
              </p>
            </section>

            {/* Threat Model */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaNetworkWired className="text-[#2186EB]" aria-hidden="true" />
                Threat Model
              </h2>
              <p className="text-gray-700 mb-4">
                Our architecture protects against:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Server compromise:</strong> Attackers gaining access to our servers see only encrypted blobs</li>
                <li><strong>Database breach:</strong> File contents remain encrypted without the key</li>
                <li><strong>Man-in-the-middle:</strong> TLS encryption protects data in transit</li>
                <li><strong>Insider threat:</strong> Employees cannot access file contents</li>
                <li><strong>Legal demands:</strong> We cannot comply with decryption requests because we cannot decrypt</li>
              </ul>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <h3 className="font-bold text-yellow-800 mb-2">Limitations</h3>
                <p className="text-yellow-700 text-sm mb-2">
                  Our security depends on:
                </p>
                <ul className="text-yellow-700 text-sm space-y-1">
                  <li>Your device not being compromised (malware could capture files before encryption)</li>
                  <li>Secure sharing of the download link (if intercepted, the file can be accessed)</li>
                  <li>Using a strong password if password protection is enabled</li>
                  <li>The underlying cryptographic algorithms remaining secure</li>
                </ul>
              </div>
            </section>

            {/* Reporting */}
            <section className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Security Reporting</h2>
              <p className="text-gray-700 mb-4">
                If you discover a security vulnerability, please report it responsibly:
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:security@miragedocs.com" className="text-[#2186EB] hover:underline">security@miragedocs.com</a>
              </p>
              <p className="text-gray-700 mt-2">
                We appreciate security researchers and will acknowledge valid reports.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 bg-gray-900 text-gray-400">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 text-white mb-4">
            <FaLock aria-hidden="true" />
            <span className="font-bold">MirageDocs</span>
          </div>
          <div className="flex justify-center gap-6 text-sm">
            <Link to="/privacy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white">Terms of Service</Link>
            <Link to="/security" className="hover:text-white">Security</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Security;
